import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  RefreshCw,
  Pin,
  Lock,
  Users,
  Loader2,
  Heart,
  MessageSquare,
} from "lucide-react";
import {
  useForumThreadsQuery,
  useForumCategoriesQuery,
  useForumAccessQuery,
  useForumMembersQuery,
  useLikeForumThreadMutation,
  FORUM_CATEGORIES,
  type ForumThreadSummary,
  type ForumSort,
} from "../api/forumApi";
import {
  Avatar,
  Byline,
  PrimaryButton,
  SecondaryButton,
  IconButton,
  NAVY,
  GOLD,
  CREAM,
} from "../components/People";

// ── One line of the index ────────────────────────────────────────────────

function ThreadRow({
  thread,
  liked,
  likes,
  onOpen,
  onLike,
}: {
  thread: ForumThreadSummary;
  liked: boolean;
  likes: number;
  onOpen: () => void;
  onLike: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative w-full cursor-pointer border-b border-gray-100 px-6 py-5 transition-colors last:border-b-0"
      style={{ backgroundColor: thread.is_pinned ? CREAM : undefined }}
    >
      {/* Unread, as a rule down the edge rather than a dot in the text. The
          mark has to survive a row that is already tinted for being pinned,
          which a background colour cannot. */}
      <span
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ backgroundColor: thread.is_unread ? GOLD : "transparent" }}
      />

      <div className="flex items-start gap-4">
        <div className="mt-0.5 hidden sm:block">
          <Avatar person={thread.author} size={44} />
        </div>

        <div className="min-w-0 flex-1">
          {/* ── Badges ───────────────────────────────────────────────── */}
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {thread.is_pinned && (
              <span
                className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em]"
                style={{ backgroundColor: "#F6E7C6", color: "#8A6516" }}
              >
                <Pin className="h-3 w-3" />
                Pinned
              </span>
            )}
            <span
              className="rounded px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em]"
              style={{ backgroundColor: "#E8EDF4", color: NAVY }}
            >
              {thread.category_label || "General"}
            </span>
            {thread.is_locked && (
              <span className="inline-flex items-center gap-1.5 rounded bg-gray-100 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-gray-500">
                <Lock className="h-3 w-3" />
                Closed
              </span>
            )}
          </div>

          <h2
            className="text-[17px] font-black leading-snug tracking-tight transition-colors group-hover:text-[#B8862B]"
            style={{ color: NAVY }}
          >
            {thread.title}
          </h2>

          <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-gray-600">
            {thread.excerpt}
          </p>

          {/* The same byline component the discussion itself uses, so a
              member's name, badge and chapter read identically on both. */}
          <div className="mt-3">
            <Byline
              person={thread.author}
              at={thread.last_activity_at || thread.created_at}
              size="sm"
            />
          </div>
        </div>

        {/* ── Counts ─────────────────────────────────────────────────── */}
        <div className="flex shrink-0 flex-col items-end gap-2 pt-0.5">
          <button
            type="button"
            // The row underneath is a button of its own; without this a like
            // opens the discussion as well.
            onClick={(e) => {
              e.stopPropagation();
              onLike();
            }}
            title={liked ? "Remove your like" : "Like this discussion"}
            className="inline-flex items-center gap-1.5 text-[13px] font-black transition-colors"
            style={{ color: liked ? "#C8503C" : "#8A94A6" }}
          >
            <Heart className="h-4 w-4" fill={liked ? "currentColor" : "none"} />
            {likes}
          </button>

          <span className="inline-flex items-center gap-1.5 text-[13px] font-black text-[#8A94A6]">
            <MessageSquare className="h-4 w-4" />
            {thread.replies_count}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── The page ─────────────────────────────────────────────────────────────

const TABS: { key: ForumSort; label: string }[] = [
  { key: "latest", label: "Latest" },
  { key: "liked", label: "Most liked" },
  { key: "unanswered", label: "Unanswered" },
];

export default function ForumThreadsPage() {
  const navigate = useNavigate();

  /**
   * The tab, the category and the page live in the URL.
   *
   * So that a discussion's "Posted in — Costs & Prices" can link back to
   * exactly this list, the back button walks the filters a reader has
   * actually used, and a filtered view can be sent to somebody. The search
   * box stays in component state: a query string rewritten on every
   * keystroke is a history entry per character.
   */
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");

  const category = params.get("category");
  const sort = (params.get("sort") as ForumSort) || "latest";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const setParam = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      // A default is an absent parameter, not an empty one — otherwise
      // /forum and /forum?sort=latest&page=1 are two URLs for one screen.
      if (v === null || v === "" || v === "latest" || (k === "page" && v === "1")) {
        next.delete(k);
      } else {
        next.set(k, v);
      }
    });
    setParams(next, { replace: false });
  };

  const setPage = (to: number) => setParam({ page: String(Math.max(1, to)) });

  const { data: access } = useForumAccessQuery();
  const { data: members } = useForumMembersQuery();
  const { data: catalogue } = useForumCategoriesQuery();
  const { data, isLoading, isFetching, refetch } = useForumThreadsQuery({
    q: search || undefined,
    sort,
    category,
    page,
  });

  const [like] = useLikeForumThreadMutation();

  /**
   * Hearts already tapped on this screen.
   *
   * The like mutation deliberately invalidates nothing — a refetch here
   * would re-sort the list under the reader's finger on the "Most liked"
   * tab — so the tap is held locally until the next time the list is asked
   * for, at which point the server's own numbers take over again.
   */
  const [likes, setLikes] = useState<Record<number, { liked: boolean; count: number }>>({});

  // A new list is a new set of numbers; anything held from the old one is
  // now a guess about rows that may not even be on the page.
  useEffect(() => {
    setLikes({});
  }, [sort, category, page, search]);

  const threads = data?.data ?? [];

  const categories = useMemo(
    () =>
      // The catalogue is in code as well as on the server, so the sidebar
      // renders at its full height on the first paint and still stands up
      // if the counts endpoint is unreachable.
      catalogue?.categories ?? FORUM_CATEGORIES.map((c) => ({ ...c, count: 0 })),
    [catalogue],
  );

  const total = catalogue?.total ?? data?.total ?? 0;

  const likeState = (t: ForumThreadSummary) =>
    likes[t.id] ?? { liked: !!t.liked_by_me, count: t.likes_count ?? 0 };

  const toggleLike = async (t: ForumThreadSummary) => {
    const before = likeState(t);
    const after = {
      liked: !before.liked,
      count: Math.max(0, before.count + (before.liked ? -1 : 1)),
    };

    setLikes((m) => ({ ...m, [t.id]: after }));

    try {
      const res = await like({ id: t.id, liked: after.liked }).unwrap();
      setLikes((m) => ({ ...m, [t.id]: { liked: res.liked, count: res.likes_count } }));
    } catch {
      setLikes((m) => ({ ...m, [t.id]: before }));
    }
  };

  const pick = (key: string | null) => setParam({ category: key, page: null });

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* ══ Discussions ══════════════════════════════════════════════ */}
        <div className="min-w-0 flex-1">
          {/* Search sits above the tabs rather than beside them: the tabs
              and the compose button are one decision about the list, and a
              field between them reads as part of neither. */}
          <div className="mb-5 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-300" />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search discussions…"
                className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm focus:border-gray-400 focus:outline-none"
              />
            </div>
            <IconButton onClick={() => refetch()} title="Refresh">
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </IconButton>
          </div>

          <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border-b border-gray-200">
            <div className="flex items-center gap-7">
              {TABS.map((tab) => {
                const on = sort === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setParam({ sort: tab.key, page: null });
                    }}
                    className="-mb-px border-b-[3px] pb-3 text-[15px] font-bold transition-colors"
                    style={{
                      borderColor: on ? GOLD : "transparent",
                      color: on ? NAVY : "#7B8599",
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <PrimaryButton onClick={() => navigate("/forum/new")} className="mb-3">
              <Plus className="h-4 w-4" />
              Start a discussion
            </PrimaryButton>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            {isLoading ? (
              <div className="flex items-center justify-center py-24 text-gray-300">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : threads.length === 0 ? (
              <div className="px-6 py-24 text-center">
                <p className="text-sm font-bold text-gray-500">
                  {search
                    ? "Nothing matches that search."
                    : sort === "unanswered"
                      ? "Every discussion here has a reply."
                      : category
                        ? "Nothing filed under this category yet."
                        : "No discussions yet."}
                </p>
                {!search && sort !== "unanswered" && (
                  <p className="mt-1.5 text-xs text-gray-400">
                    Start the first one — it will be visible to the committee only.
                  </p>
                )}
              </div>
            ) : (
              threads.map((t) => {
                const state = likeState(t);
                return (
                  <ThreadRow
                    key={t.id}
                    thread={t}
                    liked={state.liked}
                    likes={state.count}
                    onOpen={() => navigate(`/forum/${t.id}`)}
                    onLike={() => toggleLike(t)}
                  />
                );
              })
            )}
          </div>

          {(data?.last_page ?? 1) > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">
                Page {data?.current_page} of {data?.last_page} · {data?.total} discussions
              </span>
              <div className="flex gap-2">
                <SecondaryButton
                  size="sm"
                  disabled={(data?.current_page ?? 1) <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </SecondaryButton>
                <SecondaryButton
                  size="sm"
                  disabled={(data?.current_page ?? 1) >= (data?.last_page ?? 1)}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </SecondaryButton>
              </div>
            </div>
          )}
        </div>

        {/* ══ Categories ═══════════════════════════════════════════════ */}
        <aside className="w-full shrink-0 lg:w-[320px]">
          <div
            className="border-t-[3px] bg-[#F2F3F6] px-6 py-6"
            style={{ borderTopColor: GOLD }}
          >
            <h2
              className="mb-5 text-[15px] font-black uppercase tracking-[0.12em]"
              style={{ color: NAVY }}
            >
              Categories
            </h2>

            <button
              type="button"
              onClick={() => pick(null)}
              className="flex w-full items-center justify-between gap-3 border-b border-gray-200 pb-4 text-left text-[15px] font-bold"
              style={{ color: category === null ? GOLD : NAVY }}
            >
              <span>All discussions</span>
              <span className="text-[13px] font-black">{total}</span>
            </button>

            {categories.map((c) => {
              const on = category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => pick(c.key)}
                  className="flex w-full items-start justify-between gap-3 border-b border-gray-200 py-4 text-left last:border-b-0"
                >
                  <span className="min-w-0">
                    <span
                      className="block text-[15px] font-bold leading-tight"
                      style={{ color: on ? GOLD : NAVY }}
                    >
                      {c.label}
                    </span>
                    <span className="mt-1 block text-[12px] leading-snug text-gray-500">
                      {c.blurb}
                    </span>
                  </span>
                  <span className="shrink-0 text-[13px] font-black" style={{ color: NAVY }}>
                    {c.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Who is in the room. The forum's one unusual fact — that nobody
              is enrolled here, they are here by office — belongs where the
              reader is already looking at the shape of the place. */}
          <div className="px-6 py-6 text-white" style={{ backgroundColor: NAVY }}>
            <h2 className="text-[15px] font-black uppercase tracking-[0.12em]">
              This room
            </h2>
            <p className="mt-3 text-[13px] leading-relaxed text-white/70">
              Closed to the National Policy Committee. Membership follows the
              office held today, so it changes when the office does.
              {access?.seat && (
                <>
                  {" "}
                  You are here as{" "}
                  <span className="font-bold text-white">{access.seat}</span>.
                </>
              )}
              {access?.allowed && !access?.seat && <> You are viewing this as secretariat.</>}
            </p>
            {/* The one button that is not blue, because it sits ON the
                blue panel: inverted rather than invisible. */}
            <SecondaryButton
              size="sm"
              onClick={() => navigate("/forum/members")}
              className="mt-4"
            >
              <Users className="h-3.5 w-3.5" />
              Who is here{members?.total ? ` · ${members.total}` : ""}
            </SecondaryButton>
          </div>
        </aside>
      </div>
    </div>
  );
}
